import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ChangeProposal, EntityType, PendingEntry, PendingEntryStatus } from '../../types/workflow';
import { IconButton, Pill, statusTone } from '../ui';

export const getEntityTypeLabel = (entityType: EntityType | string) => {
  switch (entityType) {
    case EntityType.STAKEHOLDER:
      return 'Stakeholder';
    case EntityType.ORGANIZATION:
      return 'Organization';
    case EntityType.ASSIGNMENT:
      return 'Assignment';
    case EntityType.WIKI_PAGE:
      return 'Wiki Page';
    default:
      return entityType;
  }
};

export const getEntityName = (entityType: EntityType | string, data: any) => {
  const d = data || {};
  switch (entityType) {
    case EntityType.STAKEHOLDER:
      return `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Unnamed Stakeholder';
    case EntityType.ORGANIZATION:
      return d.name || 'Unnamed Organization';
    case EntityType.ASSIGNMENT:
      return `${d.role || 'Role'} at ${d.organizationName || 'Organization'}`;
    case EntityType.WIKI_PAGE:
      return d.title || 'Untitled Wiki Page';
    default:
      return 'Unknown Entity';
  }
};

export const entryName = (entry: PendingEntry) => getEntityName(entry.entityType, entry.entityData);
export const proposalName = (proposal: ChangeProposal) => getEntityName(proposal.entityType, proposal.originalData);

export const isReviewable = (status: PendingEntryStatus) =>
  status === PendingEntryStatus.PENDING || status === PendingEntryStatus.UNDER_REVIEW;

const STATUS_LABEL: Record<PendingEntryStatus, string> = {
  [PendingEntryStatus.PENDING]: 'Pending',
  [PendingEntryStatus.UNDER_REVIEW]: 'In review',
  [PendingEntryStatus.APPROVED]: 'Approved',
  [PendingEntryStatus.REJECTED]: 'Rejected',
};

/** Pending amber, in review blue, approved green, rejected red */
export const StatusPill: React.FC<{ status: PendingEntryStatus }> = ({ status }) => (
  <Pill tone={statusTone(status)}>{STATUS_LABEL[status] ?? String(status).replace('_', ' ')}</Pill>
);

export function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Short name for an email: the part before the @ */
export const emailName = (email?: string) => (email ? email.split('@')[0] : undefined);

export const Pager: React.FC<{
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ page, limit, total, totalPages, onPageChange }) =>
  totalPages > 1 ? (
    <div className="flex items-center justify-between gap-3 border-t border-neutral-200/70 px-4 py-2">
      <span className="text-[12px] tabular-nums text-neutral-400">
        {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
      </span>
      <div className="flex items-center gap-1">
        <IconButton label="Previous page" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Next page" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </div>
    </div>
  ) : null;
